import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, PaperPlaneRight, Trash, ImageSquare, X } from '@phosphor-icons/react';
import { fmtDateTimeFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const timeStr = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    if (d.toDateString() === today.toDateString()) return `Aujourd'hui ${timeStr}`;
    return fmtDateTimeFR(iso);
  } catch { return ''; }
};

const FeedEntry = ({ entry, currentUserId, isAdmin, onDelete }) => {
  const canDelete = isAdmin || entry.author_id === currentUserId;
  const photoUrls = entry.photo_urls && entry.photo_urls.length > 0
    ? entry.photo_urls
    : (entry.photo_url ? [entry.photo_url] : []);
  return (
    <div data-testid={`feed-entry-${entry.id}`} style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: entry.author_role === 'admin' ? '#002FA7' : '#2E7D32', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: '700' }}>
            {(entry.author_name || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.88rem', color: '#1E2030' }}>
              {entry.author_name}
              {entry.author_role === 'admin' && (
                <span style={{ marginLeft: '6px', fontSize: '0.65rem', background: '#002FA7', color: 'white', padding: '1px 6px', borderRadius: '8px', letterSpacing: '0.3px' }}>ADMIN</span>
              )}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#9E9EA7' }}>{fmtTime(entry.created_at)}</div>
          </div>
        </div>
        {canDelete && (
          <button
            onClick={() => onDelete(entry.id)}
            data-testid={`feed-delete-${entry.id}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', padding: '4px' }}
            title="Supprimer"
          >
            <Trash size={16} />
          </button>
        )}
      </div>
      {photoUrls.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: photoUrls.length === 1 ? '1fr' : `repeat(${Math.min(photoUrls.length, 3)}, 1fr)`, gap: 6, marginBottom: entry.message ? '8px' : 0 }}>
          {photoUrls.map((url, idx) => (
            <a key={idx} href={`${API_URL}${url}?t=${entry.id}`} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
              <img
                src={`${API_URL}${url}?t=${entry.id}`}
                alt={`avancement ${idx + 1}`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                style={{ width: '100%', maxHeight: photoUrls.length === 1 ? '360px' : '200px', objectFit: 'cover', borderRadius: '8px', display: 'block' }}
              />
            </a>
          ))}
        </div>
      )}
      {entry.message && (
        <div style={{ fontSize: '0.92rem', color: '#1E2030', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{entry.message}</div>
      )}
    </div>
  );
};

export const WorksiteFeedTab = ({ project, currentUser }) => {
  const [entries, setEntries] = useState([]);
  const [message, setMessage] = useState('');
  const [photos, setPhotos] = useState([]); // [{file, preview}]
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = currentUser?.role === 'admin';

  const fetchFeed = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await axios.get(`${API_URL}/api/worksite-feed/project/${project.id}`, { withCredentials: true });
      setEntries(res.data || []);
    } catch {
      toast.error('Erreur chargement du fil');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of newFiles) {
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`Photo trop lourde (max 15 Mo) : ${f.name}`);
        continue;
      }
      accepted.push({ file: f, preview: URL.createObjectURL(f) });
    }
    if (accepted.length === 0) return;
    setPhotos(prev => [...prev, ...accepted]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const target = prev[idx];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearAllPhotos = () => {
    photos.forEach(p => { if (p.preview) URL.revokeObjectURL(p.preview); });
    setPhotos([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!message.trim() && photos.length === 0) return toast.error('Ajoutez un message ou une photo');
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append('message', message);
      photos.forEach(p => fd.append('files', p.file));
      await axios.post(`${API_URL}/api/worksite-feed/project/${project.id}`, fd, { withCredentials: true });
      setMessage('');
      clearAllPhotos();
      fetchFeed();
      toast.success(`Publie${photos.length > 1 ? ` (${photos.length} photos)` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      await axios.delete(`${API_URL}/api/worksite-feed/project/${project.id}/${id}`, { withCredentials: true });
      fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="worksite-feed-admin">
      <form onSubmit={handlePost} style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px' }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Publiez une mise a jour, une consigne ou des photos pour l'equipe du chantier..."
          data-testid="admin-feed-message-input"
          rows={2}
          style={{ width: '100%', border: '1px solid #E2E2E6', borderRadius: '6px', padding: '8px 10px', resize: 'vertical', fontSize: '0.92rem', fontFamily: 'inherit' }}
        />
        {photos.length > 0 && (
          <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {photos.map((p, idx) => (
              <div key={idx} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E2E6' }}>
                <img src={p.preview} alt={`preview ${idx + 1}`} style={{ width: '100%', height: '110px', objectFit: 'cover', display: 'block' }} />
                <button type="button" onClick={() => removePhoto(idx)} data-testid={`feed-photo-remove-${idx}`} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.65)', border: 'none', color: 'white', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: 'none' }} data-testid="admin-feed-photo-input" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="btn btn-secondary btn-sm"
            data-testid="admin-feed-photo-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Camera size={16} /> Ajouter photo(s)
          </button>
          {photos.length > 0 && (
            <>
              <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{photos.length} photo(s) selectionnee(s)</span>
              <button type="button" onClick={clearAllPhotos} className="btn btn-secondary btn-sm" data-testid="admin-feed-clear-photos">Tout retirer</button>
            </>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={posting}
            data-testid="admin-feed-post-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <PaperPlaneRight size={16} weight="bold" /> {posting ? 'Publication...' : 'Publier'}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66', background: 'white', border: '1px dashed #E2E2E6', borderRadius: '10px' }}>
          <ImageSquare size={40} style={{ opacity: 0.3, marginBottom: '10px' }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>Aucune publication sur le fil pour ce chantier.</p>
        </div>
      ) : (
        entries.map(e => <FeedEntry key={e.id} entry={e} currentUserId={currentUser?.id} isAdmin={isAdmin} onDelete={handleDelete} />)
      )}
    </div>
  );
};

export default WorksiteFeedTab;
